import { Component, effect, inject, ViewChild } from '@angular/core';
import { UserRoleStore } from './user-role.store';
import { UserRoleMedicalServiceComponent } from './user-role-medical-service/user-role-medical-service.component';
import { fb, isAllowed, routeParams, router } from '../../../../modules/shared/shared.global-service';
import { TranslatePipe } from '@ngx-translate/core';
import { parseRuleId, ruleId } from './select-user-rule/select-user-rule.store';
import { SelectUserGroupComponent } from './select-user-group/select-user-group.component';
import { SelectUserRuleComponent } from './select-user-rule/select-user-rule.component';
import { DividerModule } from '@openng/optimus-ui/divider';
import { FilterBuilder } from '../../../../modules/content/helper/filter.builder';
import { getUserGroupsFrag } from './user-role.query';
import { GroupUserObject } from '@libs/types/src/user/object-type/group-user.object';
import { OrganizationSettingTypeEnum } from '@libs/types/src/organization/enum/organization-setting-type.enum';
import { Message } from '@openng/optimus-ui/message';

@Component({
    selector: 'app-user-role',
    templateUrl: './user-role.component.html',
    styleUrls: ['./user-role.component.scss'],
    providers: [UserRoleStore],
    imports: [
        UserRoleMedicalServiceComponent,
        TranslatePipe,
        SelectUserGroupComponent,
        SelectUserRuleComponent,
        DividerModule,
        Message,
    ],
})
export class UserRoleComponent {

    public readonly store = inject(UserRoleStore);

    @ViewChild('servicesRoles') servicesRoles!: UserRoleMedicalServiceComponent;

    public form = fb.group({
        ruleIds: fb.control<string[]>([]),
        groupIds: fb.control<number[]>([]),
    });

    public globalForm = fb.group({
        globalRuleIds: fb.control<string[]>([]),
        globalGroupIds: fb.control<number[]>([]),
    });

    protected isMedicalOrganization = this.store.globalGlobalStore.organizationStore.isSettingActive(OrganizationSettingTypeEnum.HEALTHCARE_SOCIAL_SETTINGS);

    constructor() {
        effect(() => this.refresh());
    }

    public getRoles() {
        if (this.isMedicalOrganization()) {
            return {
                ...this.servicesRoles.getRoles(),
                globalGroups: (this.globalForm.value.globalGroupIds || []).map((id) => ({ id })),
                globalRules: (this.globalForm.value.globalRuleIds || []).map((rule) => parseRuleId(rule)),
            };
        } else {
            return {
                perService: false,
                groups: (this.form.value.groupIds || []).map((id) => ({ id })),
                rules: (this.form.value.ruleIds || []).map((rule) => parseRuleId(rule)),
                globalGroups: (this.globalForm.value.globalGroupIds || []).map((id) => ({ id })),
                globalRules: (this.globalForm.value.globalRuleIds || []).map((rule) => parseRuleId(rule)),
            };
        }
    }

    protected async editGroups(): Promise<void> {
        await router.navigateByUrl(`/content/organizations/${this.store.globalGlobalStore.organizationStore.selectedId()}/acl`);
    }

    protected async refresh(): Promise<void> {
        this.store.globalGlobalStore.organizationStore.selectedId();
        const groupsRes = await this.store.api.getGroupUsers(FilterBuilder.singlePage({
            userId: routeParams.id(),
        }), getUserGroupsFrag);
        const groups = groupsRes?.['items'] || [];

        const publicGroups = groups.filter((it: GroupUserObject) => !it.group.isPrivate && it.organizationId != null);
        const privateGroups = groups.filter((it: GroupUserObject) => it.group.isPrivate && it.organizationId != null);
        const publicGlobalGroups = groups.filter((it: GroupUserObject) => !it.group.isPrivate && it.organizationId == null);
        const privateGlobalGroups = groups.filter((it: GroupUserObject) => it.group.isPrivate && it.organizationId == null);

        this.form.patchValue({
            groupIds: publicGroups.map((it: GroupUserObject) => it.group.id),
            ruleIds: privateGroups.flatMap((it: GroupUserObject) => {
                return it.group.rules.map((ij) => ruleId(ij.resource, ij.action));
            }),
        });
        this.globalForm.patchValue({
            globalGroupIds: publicGlobalGroups.map((it: GroupUserObject) => it.group.id),
            globalRuleIds: privateGlobalGroups.flatMap((it: GroupUserObject) => {
                return it.group.rules.map((ij) => ruleId(ij.resource, ij.action));
            }),
        });
    }

    protected readonly isAllowed = isAllowed;
}
